// Angular Files
import { Injectable, OnDestroy } from '@angular/core';

// Other External Files
import { Observable, BehaviorSubject, Subscription } from 'rxjs';

// Payment Integration Files
import { PaymentMethodTypeEnum, PaymentMethodTypeEnumConvertor } from 'apps/public-portal/src/app/payment-integrations';
import { ConvenienceFeeConfigModel, PaymentProcessorConfig } from 'apps/public-portal/src/app/payment-integrations/base/models';

// Teller Online Files
import {
    CartDto,
    ItemDto,
    CartApiClient,
    IAddCartEventRequestDto,
    AddCartEventRequestDto,
    CartStatusEnumDto,
    CartItemWarningDto,
    PaymentMethodTypeEnumDto,
    IErrorDto,
    CartResponseDto,
    CartPaymentDetailsDto,
    AddItemResponseDto,
    ItemPaymentOptionDto,
    DuplicatePaymentWarningDto
} from 'apps/public-portal/src/app/core/api/PublicPortalApiClients';
import { ConvenienceFeeResponseDto } from 'apps/public-portal/src/app/core/api/PaymentIntegrationApiClients';

// Teller Online Library Files
import {
    CartStatusEnum,
    CartStatusEnumConvertor,
    TellerOnlineAppService,
    TellerOnlineNotificationBannerService
} from 'teller-online-libraries/core';
import { TellerOnlineMessageService, TellerOnlineWindowService } from 'teller-online-libraries/shared';

/** Default key used when configKey or tellerSystemCode is not specified */
export const DEFAULT_CONFIG_KEY = 'default';

@Injectable({
    providedIn: 'root'
})
export class CartService implements OnDestroy {
    // Updates are made locally to the _cart BehaviorSubject
    private _cart: BehaviorSubject<CartModel> = new BehaviorSubject(new CartModel());

    // The cart$ Observable can be subscribed to in order to automatically receive updates to the _cart object
    public readonly cart$: Observable<CartModel> = this._cart.asObservable();

    // Updates are made locally to the _itemAdded BehaviorSubject
    private _itemAdded: BehaviorSubject<boolean> = new BehaviorSubject(false);

    // The itemAdded$ Observable can be subscribed to in order to automatically receive updates to the _itemAdded object
    public readonly itemAdded$: Observable<boolean> = this._itemAdded.asObservable();

    private _cartTotals: BehaviorSubject<CartTotals> = new BehaviorSubject(new CartTotals());

    public readonly cartTotals$: Observable<CartTotals> = this._cartTotals.asObservable();

    public calculatingTotals: boolean = false;

    private cartSubscription: Subscription;

    /** The order with which carts sent via cart.next have been sent in*/
    private _cartVersion: number = 0;

    /** Keep track of the last warning messages that were displayed in order to not display duplicates */
    private _lastDisplayWarnings: CartItemWarningDto[];

    /** A flag indicating that we tried to update the cart's status but there was no cart to update */
    private _statusUpdatePending: CartStatusEnum;

    private _previousCartPresent: boolean = false;

    public get previousCartPresent(): boolean {
        return this._previousCartPresent;
    }

    public get cartGuid(): string {
        return this.windowService.getLocalStorageItem('cartGuid');
    }

    constructor(
        private cartApiClient: CartApiClient,
        private appService: TellerOnlineAppService,
        private messageService: TellerOnlineMessageService,
        private windowService: TellerOnlineWindowService,
        private notificationBannerService: TellerOnlineNotificationBannerService
    ) {
        windowService.addEventListener('storage', this.onWindow_storage);

        this._initializeCart();
    }

    private _initializeCart() {
        /* If we don't have a cart guid, or we're on the processing page, or the TOL context is loading, get out of here (don't make a request for the cart)
                The processing page is a "placeholder" page that doesn't require the cart in order to render as it is just a go between
                to verify the cart guid given in the url has been processed and this page never actually does anything with the cart in the session.
                We don't need the cart while the TOL context is loading because the cart is reset during this process
        */
        if (!this.cartGuid
            || this.windowService?.url?.includes('checkout/processing')
            || this.windowService?.isTOL) {
            return;
        }

        this.cartApiClient.getCart(this.cartGuid, PaymentMethodTypeEnumDto.Undetermined).subscribe(response => {
            this._processCartResponse(response);
        });

        this.cartSubscription = this.cart$.subscribe(cart => {
            this._handleCartSubscription(cart);
        });
    }

    ngOnDestroy() {
        if (this.cartSubscription) this.cartSubscription.unsubscribe();
    }

    public async retrieveUserCart() {
        const response = await this.cartApiClient.retrieveUserCart(this.cartGuid).toPromise();

        await this._processCartResponse(response);
    }

    /** Retrieve a fresh version of the cart from the DB (synced with Teller),
     * returns false if the cart requires user action */
    public async refreshCart(
        cartGuid,
        paymentMethodType?: PaymentMethodTypeEnum,
        allowConfirmationPrompt = true,
        suppressWarningDialog = false) {
        /** Whether or not the action that triggered this should proceed based on warnings
         * (if action doesn't care, the value is not used) */
        let proceedWithAction = true;

        // Set to undetermined if it was not provided as a parameter,
        // since it is required in the API
        if (!paymentMethodType) {
            paymentMethodType = PaymentMethodTypeEnum.Undetermined;
        }

        // Only refresh the cart if the guid we want is the same as the one in the session
        if (cartGuid === this.cartGuid) {
            const response = await this.cartApiClient.getCart(cartGuid,
                PaymentMethodTypeEnumConvertor.toDto(paymentMethodType)).toPromise();

            // If we're calling from a place where we care about the cart size,
            // take the cart size into account when we call _processCartResponse
            if (allowConfirmationPrompt) {
                // If there's no more items in the cart, proceedWithAction should be false
                proceedWithAction = await this._processCartResponse(response,
                    response?.cart?.items?.length > 0, response?.cart?.items?.length === 0,
                    false, suppressWarningDialog);
            } else {
                proceedWithAction = await this._processCartResponse(response,
                    false, false, false, suppressWarningDialog);
            }
        }

        return proceedWithAction;
    }

    // Public Properties and Methods
    public cartExists() {
        return this.cartGuid && ((!this.loadingCart && this._cart.getValue()) || this.loadingCart);
    }

    /** A snapshot of the cart at this point in time. Do not use this for binding */
    public get cartSnapshot() {
        return this._cart.getValue();
    }

    // This is for updating properties of the cart, not for adding, removing, or updating items
    // Use the respective methods for those
    public async updateCart(properties) {
        // Get the existing cart object
        let cart = this._cart.getValue();

        // If "items" was passed as a property, make sure we remove it because we don't want to accidentally update items this way!
        if (properties.items) delete properties.items;

        // Copy any new values from the "properties" object to the existing cart object
        cart = Object.assign(cart, properties);

        // Store change in DB
        await this.cartApiClient.populateCartUserDetails(
            cart.cartGuid,
            cart.guestEmailAddress,
            !!cart.rememberPaymentMethod,
            cart.paymentMethodId ?? null,
            properties.paymentMethodType ?? PaymentMethodTypeEnum.Undetermined,
            properties.paymentProcessor ?? null
        ).toPromise();

        // Send out the next version of cart to all subscribers (newly updated!)
        this._nextCart(cart);
    }

    // Set the cart back to open after it's been set to processing
    public async openCart() {
        // Get the existing cart object
        const cart = this._cart.getValue();

        cart.status = CartStatusEnumDto.Open;
        cart.cartStatus = CartStatusEnum.Open;

        // Store change in DB
        await this.cartApiClient.userSetCartOpen(cart.cartGuid).toPromise();

        // Send out the next version of cart to all subscribers (newly updated!)
        this._nextCart(cart);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public async addExternalItemToCart(item: any) {
        // let cart = this._cart.getValue();

        // if (!cart || !cart.cartGuid) {
        //     await this._createCart();
        //     cart = this._cart.getValue();
        // }

        // item.cartGuid = cart.cartGuid;

        // return await this.searchApiClient.addExternalItemToCart(item).toPromise();

        // TEMPORARY
        return true;
    }

    /** Common logic to be used whenever adding a business system item to the cart. */
    public async addBsiToCart(item: ItemDto, paymentOption: ItemPaymentOptionDto, customAmount: number) {
        // copy the item so we can update it before sending it to the backend
        const addItem: ItemDto = ItemDto.fromJS(item.toJSON());

        addItem.selectedPaymentOption = paymentOption;
        addItem.amountInCart = customAmount ?? paymentOption.amount;
        addItem.selectedPaymentOption.amount = addItem.amountInCart; // Ensure these 2 match

        const response = await this.addItemToCart(addItem, true);

        // Fetch itemGuid generated by the backend
        item.itemGuid = response.newItem.itemGuid;
        // If it all worked out, update the search result to reflect the changes
        item.selectedPaymentOption = addItem.selectedPaymentOption;
        item.selectedPaymentOptions = [item.selectedPaymentOption];
        item.amountInCart = addItem.amountInCart;
        item.paymentOptions.find(option => option.label === item.selectedPaymentOption.label).amount = item.selectedPaymentOption.amount;
    }

    public async addItemToCart(item: ItemDto, overrideCheckout = false) {
        const cart = this._cart.getValue();
        let response: AddItemResponseDto;

        // If we don't currently have a cart, add item will create one
        await this._removeCartOnAlreadyPaidError(async () => {
            response = await this.cartApiClient.addItem(
                cart.cartGuid ?? null,
                this.appService.checkoutPage || overrideCheckout,
                item
            ).toPromise();
        });

        await this._processCartResponse(response.cart, false, false, true);

        this._itemAdded.next(true);

        return response;
    }

    public async removeItemFromCart(item: ItemDto, removeAll: boolean = true) {
        const cart = this._cart.getValue();
        let response: CartResponseDto;

        cart.items
            .filter(i => i.businessSystemItemKey === item.businessSystemItemKey)
            .forEach(i => this.notificationBannerService.dismissBanner(`warning-${i.itemGuid}`));

        await this._removeCartOnAlreadyPaidError(async () => {
            response = await this.cartApiClient.removeItem(
                cart.cartGuid,
                item.itemGuid,
                removeAll,
                this.appService.checkoutPage
            ).toPromise();
        });

        await this._processCartResponse(response, false, false, true);
    }

    public async updateItem(item: ItemDto, properties) {
        const cart = this._cart.getValue();
        let response: CartResponseDto;

        // Remove any existing warning as any update to this item should be valid and should clear existing warnings
        properties.warning = null;

        // Grab out the itemGuid and title to pass to the backend
        properties.itemGuid = item.itemGuid;
        properties.title = item.title;

        // Make a copy of the item from the properties
        const updateItem = new ItemDto(properties);
        // Update the selected payment option's amount to match the amount in the cart (so custom amount gets updated)
        if (updateItem.selectedPaymentOption) updateItem.selectedPaymentOption.amount = updateItem.amountInCart;

        await this._removeCartOnAlreadyPaidError(async () => {
            response = await this.cartApiClient.updateItem(
                cart.cartGuid,
                item.itemGuid,
                this.appService.checkoutPage,
                updateItem
            ).toPromise();
        });

        await this._processCartResponse(response, false, false, true);

        return this._cart.getValue()?.items?.find(i => i.itemGuid === item.itemGuid);
    }

    public async refreshItem(itemGuid) {
        const item = await this.cartApiClient.refreshItem(this.cartGuid, itemGuid).toPromise();
        return item;
    }

    // If we have a cartGuid in the session but no cart object yet, we are loading the cart still
    // This is used in a lot of places to check if we should respond to the cart event because the first event thrown is always an empty CartDto
    public get loadingCart() {
        return this.cartGuid && !this._cart.getValue()?.cartGuid;
    }

    public reset() {
        this._removeCartGuid();
    }

    public async addEvent(request: IAddCartEventRequestDto, paymentIdentifier: string = null) {
        await this.cartApiClient.addCartEvent(this._cart.getValue().cartGuid, paymentIdentifier, new AddCartEventRequestDto(request)).toPromise();
    }

    public async updateStatus(status: CartStatusEnum) {
        const cart = this._cart.getValue();

        if (cart.cartGuid) {
            // ensure the pending flag is set to null
            this._statusUpdatePending = null;
            const cartStatusDto = CartStatusEnumConvertor.toDto(status);

            await this.cartApiClient.updateCartStatus(cart.cartGuid, cartStatusDto).toPromise();

            // Update the status locally and push it out
            cart.status = cartStatusDto;
            cart.cartStatus = status;
            this._nextCart(cart);
            // If we don't have a cart (yet), set a flag to update it later
        } else if (this.cartGuid) {
            this._statusUpdatePending = status;
        }
    }

    public async getCartPaymentDetails(cartGuid: string): Promise<CartPaymentDetailsModel> {
        const cart: CartPaymentDetailsModel = await this.cartApiClient.getCartPaymentDetails(cartGuid).toPromise();
        cart.cartGuid = cartGuid;
        return cart;
    }

    /** Assign selected payment options due to duplicate items in the cart */
    public assignDuplicates(searchResults: ItemDto[]): void {
        const { cartSnapshot } = this;

        searchResults?.forEach(result => {
            if (result.selectedPaymentOption) {
                result.selectedPaymentOptions = [result.selectedPaymentOption];
                result.duplicates = cartSnapshot.items?.filter(i => i.businessSystemItemKey === result.businessSystemItemKey && i.itemGuid !== result.itemGuid);
                if (result.duplicates?.length > 0) {
                    result.duplicates.forEach(item => {
                        result.selectedPaymentOptions.push(item.selectedPaymentOption);
                    });
                }
            }
        });
    }

    public checkCartDependenciesSatisfied(item: ItemDto): boolean {
        return item.dependencies?.every(d => {
            const matchingItem = this.cartSnapshot?.items?.find(i => i.businessSystemItemKey === d.bsiKey && i.page.businessSystemNo === item.page.businessSystemNo);
            return matchingItem && matchingItem.amountInCart >= d.amount;
        });
    }

    /*  Iterate through all the search results for items in the cart, and update the items as needed */
    public static updateSearchResults(cart: CartModel, results: ItemDto[]) {
        results?.forEach(result => {
            // Find the item from the cart that matches this item in the search results
            // See PROD-62 and PROD-111 for reference.
            // Back-end generates a new itemGuid before adding to cart,
            // so we need to compare items using the same criteria that we use in the back-end
            const item = cart.items?.find(cartItem => cartItem.businessSystemItemKey === result.businessSystemItemKey
                && cartItem?.page.businessSystemNo === result?.page.businessSystemNo);

            // If the item isn't found, set the amountInCart to null (this denotes it is not in the cart), and remove the payment option
            if (!item) {
                result.itemGuid = null;
                result.amountInCart = 0;
                result.selectedPaymentOption = null;
                // If the item is found, update the amountInCart and selectedPaymentOptions to reflect what has been chosen
            } else {
                result.itemGuid = item.itemGuid;
                result.amountInCart = item.amountInCart;
                result.selectedPaymentOption = item.selectedPaymentOption;
            }
        });
    }

    //
    // Event Handlers
    //
    onWindow_storage = async (e: StorageEvent) => {
        if (e.key === 'cartGuid') {
            if (!e.newValue) {
                this._nextCart(new CartDto());
            } else {
                this._previousCartPresent = true;
                const cart = this._cart.getValue();
                if (!cart || cart.cartGuid !== e.newValue) {
                    this.refreshCart(e.newValue);
                }
            }
        }
    };

    //
    // Private Methods

    /** If there are any warnings, maps the warnings to the appropriate cart items */
    private _associateWarnings(response) {
        if (response.warnings) {
            // Add the warnings to the items
            if (response.cart) {
                response.cart.items.forEach(item => {
                    [item.warning] = response.warnings.filter(warning => warning.requiresAction && item.itemGuid === warning.itemGuid);
                });
            }
        }
    }

    /** If there are warnings, renders a list of warnings in a modal dialog
     * if forceProceedFalse is true, it will force the return variable to be false,
     * indicating whatever triggered this action should not proceed
     */
    private async _displayWarnings(
        warnings: CartItemWarningDto[],
        requiresConfirmation = false,
        forceProceedFalse = false,
        suppressWarningDialog = false) {
        /** Whether or not the triggering process should proceed based on the received warnings/user response */

        // Wallet pre-flight: a native payment sheet (Apple/Google Pay) is open, so any
        // MatDialog would render behind it and deadlock the flow (the user can't see or
        // dismiss it, and completePayment never fires). Never open a dialog here — a wallet
        // cryptogram is bound to a pre-authorized amount, so any warning aborts the payment;
        // the caller surfaces a "review your cart" message after the sheet closes. Evaluated
        // before the _duplicateWarning shortcut so an unresolved warning can't slip through
        // on a retry.
        if (suppressWarningDialog) {
            const hasWarnings = warnings && warnings.length > 0;
            if (hasWarnings) {
                this._addWarningBanners(warnings);
            }
            return !hasWarnings && !forceProceedFalse;
        }

        // if the last warning was exactly the same skip it
        if (this._duplicateWarning(warnings)) {
            return true;
        }

        let proceed = true;

        if (warnings && warnings.length > 0) {
            const title = 'Cart Warnings';
            let html = '<ul>';
            warnings.forEach(warning => {
                html += `<li>${warning.message}</li>`;
            });
            html += '</ul>';

            if (requiresConfirmation) {
                const requiresAction = warnings.filter(warn => warn.requiresAction);

                if (requiresAction.length > 0) {
                    html += '<p>Please resolve the above warnings to continue.<p>';
                    await this.messageService.alert(html, title);
                    proceed = false;
                } else {
                    html += '<p>Do you want to proceed and pay?</p>';
                    proceed = await this.messageService.prompt(html, title);
                }
            } else {
                await this.messageService.alert(html, title);
            }

            this._addWarningBanners(warnings);
        }

        if (forceProceedFalse) proceed = false;

        return proceed;
    }

    /** Add a persistent, resolvable notification banner for each warning that requires action. */
    private _addWarningBanners(warnings: CartItemWarningDto[]) {
        warnings.forEach(warning => {
            if (warning && warning.requiresAction) {
                this.notificationBannerService.addBanner({
                    message: warning.message,
                    tag: `warning-${warning.itemGuid}`,
                    bannerClass: 'notification-banner--caution',
                    svgIcon: 'attention',
                    svgIconClass: 'teller-online-icon--caution',
                    resolveAction: {
                        href: `/cart/${warning.itemGuid}`,
                        text: 'Resolve Now'
                    },
                    dismissAction: {
                        text: 'Resolve Later'
                    },
                    requiresAuthentication: true
                });
            }
        });
    }

    /** Prevent duplicate warning dialogs from being displayed by comparing warnings against the lastDisplayWarnings.
     * If warnings is null, _lastDisplayWarnings will be cleared out so the same warning could be displayed again
     */
    private _duplicateWarning(warnings: CartItemWarningDto[]): boolean {
        let isDuplicate = false;

        // If we have previously displayed a warning, and we have new warnings to display,
        // check if they are the same
        if (this._lastDisplayWarnings && warnings?.length > 0) {
            // Grab out just the messages from the previous warnings to compare against our new ones
            const warningMessages = this._lastDisplayWarnings.map(i => i.message);

            // If the number of warnings is the same and every new message was in the previous list of warnings
            // these are duplicate warnings
            isDuplicate = warnings?.length === this._lastDisplayWarnings?.length
                && warnings.every(element => warningMessages.includes(element.message));
        }

        // If this wasn't a duplicate, update the warnings to match the new warnings that were received
        if (!isDuplicate) {
            this._lastDisplayWarnings = warnings?.length > 0 ? warnings : null;
        }

        return isDuplicate;
    }

    /** Associate any warnings returned in the response with the items,
     * display the warnings as either an alert (ok) or a prompt (yes/no) based on requiresConfirmation flag
     * if forceProceedFalse is true, it will force the return variable to be false,
     * indicating whatever triggered this action should not proceed
     */
    private async _processCartResponse(
        response,
        requiresConfirmation = false,
        forceProceedFalse = false,
        setLastModified = false,
        suppressWarningDialog = false) {
        this._associateWarnings(response);

        const proceedWithAction = await this._displayWarnings(
            response.warnings, requiresConfirmation, forceProceedFalse, suppressWarningDialog);

        if (response.cart) {
            this._nextCart(response.cart);
            this._setCartGuid(response.cart.cartGuid);
        } else if (this.cartGuid) {
            // If we did not get a cart back, remove the cartGuid
            this._removeCartGuid();
        }

        if (setLastModified) {
            // set local storage item for cart last modified to current timestamp
            this.windowService.setLocalStorageItem('cartLastModified', new Date().getTime().toString());
        }

        return proceedWithAction;
    }

    /** Handle any new updates to the cart via _cart.next() */
    private _handleCartSubscription(cart) {
        this.appService.consoleLog(this, cart, `Cart Version: ${cart?.version}`);

        // If this is an older version of the cart, we don't want it
        if (cart?.version && cart.version < this._cartVersion) return;

        // Only respond once we've finished loading the cart (the first event will always be an empty CartDto)
        // Also, only respond if we have a cartGuid in the session, if we don't, no need to respond to this event
        if (!this.loadingCart && this.cartGuid) {
            if (!cart) {
                this._removeCartGuid();
            } else {
                this._checkCartValid(cart);
                if (this._statusUpdatePending) {
                    this.updateStatus(this._statusUpdatePending);
                }
            }
        }
    }

    /** If the cart is in an "invalid" state (something the user can't do anything about), removes it from the session */
    private _checkCartValid(cart: CartModel) {
        const validCartStates = [
            CartStatusEnum.Open,
            CartStatusEnum.Processing,
            CartStatusEnum.CheckingOut
        ];

        const validCartState = validCartStates.includes(cart.cartStatus);

        // Check for a cartGuid because if we "don't have a cart" it's an empty dto which is still truthy
        if (cart && cart.cartGuid && !validCartState) {
            this._removeCartGuid();
        }
    }

    private async _createCart() {
        // await this.cartApiClient.createCart().toPromise().then((response) => {
        //     this._nextCart(response);
        //     this._setCartGuid(response.cartGuid);
        // });
    }

    /** Removes the cartGuid token from the session and sends out an updated empty cartDto */
    private _removeCartGuid() {
        // Only attempt to remove the guid (and push an empty cart) if we have a guid to remove
        if (this.cartGuid) {
            this._previousCartPresent = true;
            // Remove the guid first so that when we push the empty cart, things can respond appropriately
            this.windowService.removeLocalStorageItem('cartGuid');

            this._nextCart(new CartDto());
        }
    }

    private _setCartGuid(cartGuid: string) {
        this._previousCartPresent = true;
        this.windowService.setLocalStorageItem('cartGuid', cartGuid);
    }

    /** Sends out the next cart to the Behavior Subject */
    private _nextCart(cart: CartDto) {
        this._cartVersion++;
        if (cart) {
            cart.version = this._cartVersion;
        }

        const cartModel = this._generateItemsForDisplay(cart);

        if (cartModel.items?.find(i => i.warning?.requiresAction)) {
            cartModel.checkoutEnabled = false;
        }

        this._cart.next(cartModel);
    }

    private _generateItemsForDisplay(cart: CartDto) {
        const cartModel: CartModel = new CartModel(cart);

        if (cartModel) {
            cartModel.cartStatus = CartStatusEnumConvertor.fromDto(cart.status);
            // Generate the display list from all non-duplicates items
            if (cartModel.items) {
                cartModel.itemsForDisplay = [];

                cartModel.items.forEach(item => {
                    // If this BSI isn't already in the list, add the item to the list (or if it's a misc item, always add it)
                    if (!cartModel.itemsForDisplay.filter(i => i.businessSystemItemKey != null && i.businessSystemItemKey === item.businessSystemItemKey).length) {
                        cartModel.itemsForDisplay.push(item);
                        // If it's already in the list, we can't check out with this cart
                    } else {
                        cartModel.checkoutEnabled = false;
                    }
                });

                // Partition the cart into an object keyed on both tellerSystemCode and configKey, valued with arrays of items
                cartModel.partitions = cartModel.itemsForDisplay.reduce((partitions, item) => {
                    const configKey = item.page.configKey || null;
                    const tellerSystemCode = item.page.tellerSystemCode || null;
                    const key = `${tellerSystemCode ?? DEFAULT_CONFIG_KEY}-${configKey ?? DEFAULT_CONFIG_KEY}`;

                    partitions[key] ??= { tellerSystemCode, configKey, items: [] };
                    partitions[key].items.push(item);

                    return partitions;
                }, {} as { [key: string]: CartPartitionEntry });
            }
        }
        return cartModel;
    }

    private async _removeCartOnAlreadyPaidError(method) {
        let error: IErrorDto;
        try {
            await method();
        } catch (e) {
            if (e.errorDef) {
                error = e as IErrorDto;
                if (error.errorDef === 'CartAlreadyPaid' || error.errorDef === 'CartNonExistent') {
                    this._removeCartGuid();
                }
                throw e;
            }
        }
    }

    /**
     * Check for potential duplicate payments before checkout.
     * Returns warnings if any cart items have matching identifiers in pending payments.
     * @returns List of duplicate payment warnings, or empty array if no duplicates found
     */
    public async checkForDuplicates(): Promise<DuplicatePaymentWarningDto[]> {
        const { cartGuid } = this;
        if (!cartGuid) {
            return [];
        }

        try {
            const warnings = await this.cartApiClient.checkForDuplicates(cartGuid).toPromise();
            return warnings || [];
        } catch (error) {
            console.error('Error checking for duplicate payments:', error);
            // Don't block checkout if the duplicate check fails
            return [];
        }
    }

    /**
     * Check for duplicate payments and display a warning dialog if any are found.
     * @returns true if user wants to proceed (no duplicates or user confirmed), false if user cancelled
     */
    public async checkAndWarnForDuplicates(): Promise<boolean> {
        const warnings = await this.checkForDuplicates();

        if (warnings.length === 0) {
            return true;
        }

        // Build the warning message
        let html = '<p><strong>Warning:</strong> The following item(s) may already have a payment in progress:</p><ul>';
        warnings.forEach(warning => {
            html += `<li>${warning.message}</li>`;
        });
        html += '</ul>';
        html += '<p>If you continue, you may be charged twice for the same item(s).</p>';
        html += '<p>Do you want to proceed anyway?</p>';

        // Show confirmation dialog
        const proceed = await this.messageService.prompt(html, 'Potential Duplicate Payment');

        return proceed;
    }

    /** Build the per-configKey `ConvenienceFeeConfigModel` map that
     *  {@link calculateTotals} expects. Takes the payment provider's default and
     *  override configs explicitly (rather than the provider itself) to avoid a
     *  circular import with `payment-processor.provider`.
     */
    public buildFeeConfigs(
        defaultConfig: PaymentProcessorConfig,
        configOverrides: { [key: string]: PaymentProcessorConfig }
    ): { [key: string]: ConvenienceFeeConfigModel } {
        const feeConfigs: { [key: string]: ConvenienceFeeConfigModel } = {
            [DEFAULT_CONFIG_KEY]: defaultConfig?.convenienceFee,
        };
        Object.keys(configOverrides).forEach(key => {
            const override = configOverrides[key]?.convenienceFee;
            if (override) feeConfigs[key] = override;
        });
        return feeConfigs;
    }

    /** Calculate cart totals (subtotal, convenience fees, total) per partition.
     * @param feeConfigs Convenience fee config per config key
     * @param calculateFee Callback that returns the API-calculated fees (avoids circular dependency on PaymentProcessorProvider)
     */
    public async calculateTotals(
        feeConfigs: { [key: string]: ConvenienceFeeConfigModel },
        calculateFee: () => Promise<ConvenienceFeeResponseDto>
    ): Promise<void> {
        this.resetTotals();
        const cart = this.cartSnapshot;

        if (!cart?.cartGuid || !cart?.items?.length) return;

        this.calculatingTotals = true;

        const response = await calculateFee();
        const calculatedFees = response?.fees ?? [];

        const totals = new CartTotals();

        // Cart-level fee override (set by a prepare endpoint that returned the
        // whole-cart fee total). Lives outside the per-partition pipeline —
        // surfaced through CartTotals' aggregate getters. The per-partition
        // loop below stays focused on per-partition (percent / flat / other)
        // math; it doesn't see the override at all.
        totals.aggregateConvenienceFeeOverride = feeConfigs[DEFAULT_CONFIG_KEY]?.aggregateOverride ?? null;

        Object.keys(cart.partitions).forEach(partitionKey => {
            const partition = cart.partitions[partitionKey];
            const configKey = partition.configKey ?? DEFAULT_CONFIG_KEY;
            const tellerSystemCode = partition.tellerSystemCode ?? DEFAULT_CONFIG_KEY;
            // When an override's fee config is missing AND the default uses
            // plugin-driven fees, fall back to the default config. Plugin-driven
            // fees are computed per-partition server-side, so the default config
            // is just a display shell for any override that doesn't have its own.
            // We deliberately do NOT fall back when the default is percent/flat —
            // that would apply the default's rate to an override that may have
            // intentionally different (or no) fees configured.
            const defaultFeeConfig = feeConfigs[DEFAULT_CONFIG_KEY];
            const feeConfig = feeConfigs[configKey]
                ?? (defaultFeeConfig?.isPluginDriven ? defaultFeeConfig : undefined);

            totals.subtotal[partitionKey] = 0;
            partition.items.forEach(item => {
                totals.subtotal[partitionKey] += item.amountInCart;
            });

            totals.total[partitionKey] = totals.subtotal[partitionKey];

            if (feeConfig?.enabled) {
                // match the fee by both systemCode and configKey
                const apiFee = calculatedFees.find(
                    f => (f.configKey === '' ? DEFAULT_CONFIG_KEY : f.configKey) === configKey
                      && (f.tellerSystemCode ?? DEFAULT_CONFIG_KEY) === tellerSystemCode
                )?.amount;

                switch (feeConfig.format) {
                    case 'percent':
                    case 'other':
                        // apiFee can be undefined when the fee API returns no
                        // entry for this partition — common when the recalc
                        // raced ahead of a payment-method-type switch and the
                        // backend computed fees for the previous type. Store
                        // null so aggregateTotal short-circuits to null
                        // instead of producing NaN from `subtotal + undefined`.
                        if (apiFee != null) {
                            totals.convenienceFees[partitionKey] = apiFee;
                            totals.total[partitionKey] += apiFee;
                        } else {
                            totals.convenienceFees[partitionKey] = null;
                            totals.total[partitionKey] = null;
                        }
                        break;
                    case 'flat':
                        totals.convenienceFees[partitionKey] = feeConfig.rate;
                        totals.total[partitionKey] += totals.convenienceFees[partitionKey];
                        break;
                    default:
                        totals.convenienceFees[partitionKey] = null;
                        totals.total[partitionKey] = null;
                        break;
                }
            }
        });

        this._cartTotals.next(totals);
        this.calculatingTotals = false;
    }

    public resetTotals(): void {
        this._cartTotals.next(new CartTotals());
    }
}

export class CartTotals {
    subtotal: { [key: string]: number } = {};

    convenienceFees: { [key: string]: number } = {};

    total: { [key: string]: number } = {};

    /**
     * Cart-level fee override delivered by a prepare endpoint (FIS, Generic,
     * saved-payment-method flows). When non-null, the {@link aggregateConvenienceFee}
     * and {@link aggregateTotal} getters surface it directly instead of summing
     * per-partition values — per-partition fees aren't computed in this case.
     */
    aggregateConvenienceFeeOverride: number | null = null;

    get partitionCount(): number {
        return Object.keys(this.subtotal).length;
    }

    get aggregateSubtotal(): number {
        const values = Object.values(this.subtotal);
        return values.length ? values.reduce((sum, v) => sum + (v ?? 0), 0) : null;
    }

    get aggregateConvenienceFee(): number {
        if (this.aggregateConvenienceFeeOverride != null) {
            return this.aggregateConvenienceFeeOverride;
        }
        const values = Object.values(this.convenienceFees);
        if (!values.length || values.some(v => v == null)) return null;
        return values.reduce((sum, v) => sum + v, 0);
    }

    get aggregateTotal(): number {
        // The override is the whole cart's fee and replaces the per-partition fees, which are
        // either unknown (null) or would be counted twice (TOP-3143).
        if (this.aggregateConvenienceFeeOverride != null) {
            const subtotal = this.aggregateSubtotal;
            return subtotal == null ? null : subtotal + this.aggregateConvenienceFeeOverride;
        }

        const values = Object.values(this.total);
        if (!values.length || values.some(v => v == null)) return null;
        return values.reduce((sum, v) => sum + v, 0);
    }
}

export class CartPaymentDetailsModel extends CartPaymentDetailsDto {
    cartGuid?: string;
}

export interface CartPartitionEntry {
    tellerSystemCode: string | null;
    configKey: string | null;
    items: ItemDto[];
}

export class CartModel extends CartDto {
    /** Copy of the items property but for display purposes to the user.
     * This will exclude any duplicate items
     */
    cartStatus: CartStatusEnum;

    itemsForDisplay?: ItemDto[];

    checkoutEnabled: boolean = true;

    partitions: { [key: string]: CartPartitionEntry } = {};
}
